import {
  CRM_FIELD_OP_LABEL,
  FIELD_PREFIX,
  fieldLabelKey,
  parseAccountStatusField,
  parseAccountStatusValue,
  parseFieldCondition,
} from '@/utils/crm-section-clauses.util';
// Readable active-filter chips for the per-CRM filter sections (CP-6).
//
// The per-CRM sections (``filters.crmFilterSections``, CP-5b) store each
// filter as a raw ``CrmFilterClause`` whose ``condition`` is the
// /crm/exclusion-set token — e.g. ``current_customers`` or the opaque
// ``owner:005g0000001abcXAAQ``. Surfacing those raw tokens on the chip
// strip is unreadable ("owner:005g…"), so this module turns each clause
// into a human chip: a CRM short label + polarity prefix + a friendly
// condition label, with owner ids resolved to names.
//
// Kept PURE and free of React/RTK so it can be unit-tested in isolation and
// reused outside the hook. Owner-id → name resolution is threaded in via the
// ``CrmFilterChipContext`` (the caller populates it from the cached CRM-users
// query); this module never calls a hook.

import type { ActiveFilter } from '@/components/search/search-shell.component';
import type {
  CrmFilterClause,
  CrmFilterCrm,
  CrmFilterSections,
} from '@/types/search-filters';

/** Defensive default — a saved/hydrated search may predate
 *  ``crmFilterSections``. Always read sections through this. */
const EMPTY_CRM_FILTER_SECTIONS: Record<CrmFilterCrm, CrmFilterClause[]> = {
  salesforce: [],
  hubspot: [],
};

/** Short labels for the chip prefix (kept compact so the chip strip
 *  doesn't overflow — "SF owner Levon Adamyan" not "Salesforce owner …"). */
export const CRM_SHORT_LABEL: Record<CrmFilterCrm, string> = {
  salesforce: 'SF',
  hubspot: 'HS',
};

/** Each CRM's name, for section headers and control names. */
export const CRM_LABEL: Record<CrmFilterCrm, string> = {
  salesforce: 'Salesforce',
  hubspot: 'HubSpot',
};

/** Chip key prefix for a per-CRM section clause. The full key is
 *  ``crmsec:<crm>:<clauseId>`` — it encodes the owning CRM and the clause's
 *  stable client id so the chip's X can delete THAT exact clause from
 *  ``filters.crmFilterSections[crm]`` (mirrors the ``cmp:`` routing the
 *  composite firmographic chips use). */
export const CRM_SECTION_CHIP_PREFIX = 'crmsec:';

/** Per-CRM owner-id → display-name maps, so an ``owner:<id>`` clause can
 *  render the owner's name instead of the opaque CRM id. Populated by the
 *  caller from the cached ``getCrmUsers`` query for each connected CRM. A
 *  missing map (or a missing id within it) falls back to the raw id. */
export interface CrmFilterChipContext {
  ownerNames?: Partial<Record<CrmFilterCrm, Record<string, string>>>;
  /** Per-CRM field labels for ``field:`` clauses, keyed ``<object>.<Field>``
   *  (``lead.Status`` → ``Lead Status``). A field without a label keeps its
   *  token on the chip. */
  fieldLabels?: Partial<Record<CrmFilterCrm, Record<string, string>>>;
}

/** Build the chip key for a section clause. */
export function crmSectionChipKey(crm: CrmFilterCrm, clauseId: string): string {
  return `${CRM_SECTION_CHIP_PREFIX}${crm}:${clauseId}`;
}

/** Parse a ``crmsec:<crm>:<clauseId>`` key back into its parts, or null when
 *  the key isn't a CRM-section chip. Used by the chip remover to route the X
 *  click to the right CRM's clause list. ``clauseId`` may itself contain
 *  colons (UUIDs don't, but be safe) — split only on the first two. */
export function parseCrmSectionChipKey(
  key: string
): { crm: CrmFilterCrm; clauseId: string } | null {
  if (!key.startsWith(CRM_SECTION_CHIP_PREFIX)) return null;
  const rest = key.slice(CRM_SECTION_CHIP_PREFIX.length);
  const sep = rest.indexOf(':');
  if (sep < 0) return null;
  const crm = rest.slice(0, sep) as CrmFilterCrm;
  const clauseId = rest.slice(sep + 1);
  if ((crm !== 'salesforce' && crm !== 'hubspot') || !clauseId) return null;
  return { crm, clauseId };
}

// ── Condition → human label ──
//
// The fixed-token conditions map to a fixed phrase; the parameterized ones
// (``owner:`` / ``account_status:`` / ``last_activity_*``) parse the value
// out and (for owner) resolve the id to a name.

const FIXED_CONDITION_LABEL: Record<string, string> = {
  current_customers: 'Current customers',
  active_opportunities: 'Active opportunities',
  owned_by_me: 'Records I own',
  owned_by_others: 'Records others own',
  // The sidebar cannot create these two, but a Clu search handoff carries them
  // (CLE-550): without a label the chip would show the raw token.
  in_crm: 'In CRM',
  owned_any: 'Owned by anyone',
  crm_accounts: 'All CRM accounts',
};

const OWNER_PREFIX = 'owner:';
const OWNED_ACTIVE_WITHIN_PREFIX = 'owned_active_within:';
const ACCOUNT_STATUS_PREFIX = 'account_status:';
const LAST_ACTIVITY_OVER_PREFIX = 'last_activity_over:';
const LAST_ACTIVITY_WITHIN_PREFIX = 'last_activity_within:';

/**
 * Human-readable label for a single CRM filter ``condition`` token, resolved
 * against ``crm`` (so an ``owner:<id>`` can look up the owner's name in the
 * per-CRM map). Pure — no CRM prefix, no polarity; that framing is added by
 * ``crmFilterClauseChipLabel``. Falls back to the raw value when a token is
 * unrecognised or an owner id can't be resolved.
 */
export function crmFilterConditionLabel(
  condition: string,
  crm: CrmFilterCrm,
  ctx?: CrmFilterChipContext
): string {
  const token = condition.trim();

  const fixed = FIXED_CONDITION_LABEL[token];
  if (fixed) return fixed;

  if (token.startsWith(FIELD_PREFIX)) {
    // ``field:<object>.<Field>:<op>[:<value>]``. Without the CRM's label for
    // the field the token itself is the clearest thing to show.
    const parsed = parseFieldCondition(token);
    const label =
      parsed &&
      ctx?.fieldLabels?.[crm]?.[fieldLabelKey(parsed.object, parsed.field)];
    if (parsed && label) {
      const op = CRM_FIELD_OP_LABEL[parsed.op];
      return parsed.value === null
        ? `${label} ${op}`
        : `${label} ${op} ${parsed.value}`;
    }
    return token;
  }

  if (token.startsWith(OWNER_PREFIX)) {
    const id = token.slice(OWNER_PREFIX.length);
    const name = ctx?.ownerNames?.[crm]?.[id];
    // Fall back to the raw id when unresolved (owner map not loaded yet, or
    // the id is from a now-removed CRM user). Better an opaque id than a
    // dropped chip the user can't see to remove.
    return `owner ${name || id}`;
  }

  if (token.startsWith(ACCOUNT_STATUS_PREFIX)) {
    const field = parseAccountStatusField(token);
    return `status: ${field ? `${field}: ` : ''}${parseAccountStatusValue(token) ?? ''}`;
  }

  if (token.startsWith(OWNED_ACTIVE_WITHIN_PREFIX)) {
    // ``owned_active_within:<days>`` (a Clu handoff token, like the two above).
    // A day count that is not a positive integer falls through to the verbatim
    // unknown-token label below rather than reading as a number it is not.
    const days = token.slice(OWNED_ACTIVE_WITHIN_PREFIX.length);
    if (/^[1-9]\d*$/.test(days))
      return `Owned, active in last ${days} ${days === '1' ? 'day' : 'days'}`;
  }

  if (token.startsWith(LAST_ACTIVITY_OVER_PREFIX)) {
    // ``last_activity_over:<days>`` or ``last_activity_over:<days>:<scope>``.
    // Only the day count drives the label; the optional scope is ignored
    // here (it disambiguates the resolution server-side, not the chip).
    const tail = token.slice(LAST_ACTIVITY_OVER_PREFIX.length);
    const days = tail.split(':')[0];
    return `inactive >${days}d`;
  }

  if (token.startsWith(LAST_ACTIVITY_WITHIN_PREFIX)) {
    // Strip the optional ``:<scope>`` suffix like the OVER branch above, else a
    // scoped clause renders a malformed chip ("active ≤45:contactsd").
    const days = token.slice(LAST_ACTIVITY_WITHIN_PREFIX.length).split(':')[0];
    return `active ≤${days}d`;
  }

  // Unknown token — show it verbatim rather than dropping the chip.
  return token;
}

/**
 * Full chip label for a clause: CRM short label + the condition label.
 * POLARITY-NEUTRAL — it does NOT prepend "not". The exclude negation is
 * supplied by the pill itself (``variant: 'exclude'`` in buildCrmFilterChips
 * renders the "NOT" treatment); prefixing "not" here too double-negates
 * ("NOT not SF owner …"). Reads "SF owner Levon Adamyan" /
 * "HS: Current customers"; the pill frames an exclude chip as
 * "NOT SF owner Levon Adamyan".
 *
 * The colon framing differs by condition kind so it reads naturally:
 *  - owner / last-activity conditions continue the CRM short label with no
 *    colon ("SF owner …", "SF inactive >30d").
 *  - the fixed phrases + status are standalone clauses and take a colon
 *    ("HS: Current customers", "SF: status: Churned").
 */
export function crmFilterClauseChipLabel(
  clause: CrmFilterClause,
  crm: CrmFilterCrm,
  ctx?: CrmFilterChipContext
): string {
  const conditionLabel = crmFilterConditionLabel(clause.condition, crm, ctx);
  const short = CRM_SHORT_LABEL[crm];

  const token = clause.condition.trim();
  const inlineWithCrm =
    token.startsWith(OWNER_PREFIX) ||
    token.startsWith(LAST_ACTIVITY_OVER_PREFIX) ||
    token.startsWith(LAST_ACTIVITY_WITHIN_PREFIX);

  return inlineWithCrm
    ? `${short} ${conditionLabel}`
    : `${short}: ${conditionLabel}`;
}

/**
 * Build one readable ``ActiveFilter`` chip per clause across every per-CRM
 * section. The chip:
 *  - ``key``         = ``crmsec:<crm>:<clauseId>`` (precise removal routing).
 *  - ``value``       = the raw condition token (stable chip identity).
 *  - ``displayValue``= the human label (what the pill renders).
 *  - ``variant``     = 'exclude' for exclude clauses (red treatment).
 *
 * Renders a chip for every clause that has a usable ``id`` — including one whose
 * ``condition`` is blank/whitespace. A blank clause still fails validation
 * before search/import, so it MUST stay visible and removable (via its unique
 * ``crmsec:<crm>:<clauseId>`` key) rather than being silently dropped, which
 * would leave the user unable to clear a filter that blocks their search.
 * Defaults a missing sections object to the empty per-CRM map.
 */
export function buildCrmFilterChips(
  filters: { crmFilterSections?: CrmFilterSections },
  ctx?: CrmFilterChipContext
): ActiveFilter[] {
  const sections = filters.crmFilterSections ?? EMPTY_CRM_FILTER_SECTIONS;
  const chips: ActiveFilter[] = [];
  (Object.keys(EMPTY_CRM_FILTER_SECTIONS) as CrmFilterCrm[]).forEach((crm) => {
    const clauses = Array.isArray(sections[crm]) ? sections[crm] : [];
    for (const clause of clauses) {
      // A usable ``id`` is what routes the chip's X to the exact clause; without
      // one the chip could not be removed, so skip only those.
      if (!clause || typeof clause.id !== 'string' || !clause.id.trim())
        continue;
      const hasCondition =
        typeof clause.condition === 'string' &&
        clause.condition.trim().length > 0;
      chips.push({
        // Unique per clause id so the X deletes THAT clause even when two
        // blank clauses share an (empty) condition.
        key: crmSectionChipKey(crm, clause.id),
        // Group by CRM (not a generic 'CRM') so the top strip collapses
        // multiple clauses per-CRM into "Salesforce N" / "HubSpot N" rather
        // than lumping SF + HS together — mirrors the per-CRM filter sections.
        // A lone clause still renders its full readable displayValue.
        label: crm === 'salesforce' ? 'Salesforce' : 'HubSpot',
        value: hasCondition ? clause.condition : clause.id,
        displayValue: hasCondition
          ? crmFilterClauseChipLabel(clause, crm, ctx)
          : 'Incomplete filter',
        variant: clause.polarity === 'exclude' ? 'exclude' : 'default',
      });
    }
  });
  return chips;
}
